import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import getRecaptchaToken from '../helpers/getRecaptchaToken';

import '../styles/AccountRecoveryPage.css';
import AppContext from './context/AppContext';
import { bool } from 'prop-types';

const AccountRecoveryPage = ({ isMobile, isLoggedIn }) => {
    const { appContext } = useContext(AppContext);
    const navigate = useNavigate();
    const [isValidRequest, setIsValidRequest] = useState(true);
    const [isConfirming, setIsConfirming] = useState(true);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [startRecovery, setStartRecovery] = useState(false);
    const [email, setEmail] = useState('');
    const handleVerificationRequest = useCallback(async () => {
        try {
            if (!email
                || !isEmail(email)) {
                setIsValidRequest(false);
                setIsConfirming(false);
                return;
            }
            setIsConfirming(true);
            setIsConfirmed(false);
            setErrorMessage(null);
            const response = await fetch(new URL('/api/user/recover', process.env.APP_URL), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    token: await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                    email,
                }),
            });
            const data = await response.json();
            setIsConfirming(false);
            if (!data.success) {
                if (data.error) {
                    setErrorMessage(data.error);
                }
                setIsConfirmed(false);
                return;
            }
            setIsConfirmed(true);
        } catch (e) {
            setIsConfirming(false);
            setErrorMessage(e.message);
        }
    }, [email]);
    useEffect(() => {
        if (startRecovery === true
            && isLoggedIn === false) {
            handleVerificationRequest();
        }
    }, [startRecovery, isLoggedIn, handleVerificationRequest]);
    useEffect(() => {
        const navigateTimeoutFunc = () => {
            navigate('/');
        };
        const navigateTimer = null;
        const NAVIGATE_TIMEOUT_MS = 3000;
        if (isConfirmed === true
            || isLoggedIn === true) {
            setTimeout(navigateTimeoutFunc, NAVIGATE_TIMEOUT_MS);
        }
        return () => {
            clearTimeout(navigateTimer);
        };
    }, [isConfirmed, isLoggedIn]);
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email || !isEmail(email)) 
            return;
        setStartRecovery(true);
    };
    return (<Container
        fluid>
        <div
            className={'page--section mt-4'}>
            <h2>Recover Account</h2>
            {(startRecovery === false
                && isLoggedIn === false) && (<>
                <Form
                    onSubmit={handleSubmit}>
                    <Form.Group
                        className={'mb-2'}
                        controlId={'accountRecoveryForm.Email'}>
                        <Form.Label>Your email</Form.Label>
                        <Form.Control
                            style={{ maxWidth: '300px' }}
                            type={'text'}
                            name={'email'}
                            placeholder={'Enter your user email...'}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            isValid={isEmail(email)}
                            isInvalid={email !== '' && !isEmail(email)} />
                    </Form.Group>
                    <Form.Group
                        controlId={'accountRecoveryForm.Submit'}>
                        <Button
                            type={'submit'}
                            onClick={handleSubmit}
                            disabled={!email || !isEmail(email)}>Submit</Button>
                    </Form.Group>
                </Form>
            </>)}
            {isLoggedIn === true && (<p>You are already logged in.</p>)}
            {startRecovery === true && (<>
                {!isValidRequest && (<p>Invalid recovery request.</p>)}
                {isConfirming === true && (<p>Confirming request...</p>)}
                {(isConfirming === false && isConfirmed === false) && (<p>Request failed.</p>)}
                {isConfirmed === true && (
                    <p
                        style={{ color: 'green' }}>Your request has been received. Please check your email to continue with the process.</p>
                )}
                {(!isConfirming && errorMessage !== null) && (
                    <p
                        style={{ color: 'red' }}>Error: {errorMessage ?? 'Failed.'}</p>
                )}
            </>)}
        </div>
    </Container>);
};

AccountRecoveryPage.propTypes = {
    isMobile: bool,
    isLoggedIn: bool,
};

export default AccountRecoveryPage;